<template>
  <HrPageLayout
    title="Training Management"
    subtitle="Manage training programs, schedules and enrollments"
    icon="🎓"
    :breadcrumb-items="breadcrumbItems"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting" @click="exportReportsCsv">
        <i class="bx bx-download"></i> Reports CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exportingPdf" @click="exportReportsPdf">
        <i class="bx" :class="exportingPdf ? 'bx-loader-alt bx-spin' : 'bx-file'"></i> Reports PDF
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="$router.push('/trainings-catalog')">
        <i class="bx bx-book"></i> Trainings Catalog
      </button>
    </template>

    <div class="training-management-content">
      <div class="hr-content-grid grid-cards">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-book-reader"></i> Training Programs</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">View and manage all training courses in the catalog.</p>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="$router.push('/trainings-catalog')">Open Catalog</button>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-group"></i> Employee Certifications</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">Record and track employee certifications.</p>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="$router.push('/employee-certifications')">Manage Certifications</button>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-bar-chart"></i> Training Stats</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-d-flex hr-justify-between hr-mb-2"><span class="hr-text-muted">Total</span><strong>{{ trainings.length }}</strong></div>
            <div class="hr-d-flex hr-justify-between"><span class="hr-text-muted">Active</span><strong>{{ activeCount }}</strong></div>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-file"></i> Staff training applications</h5>
          </div>
          <div class="hr-chart-body">
            <p class="hr-text-muted hr-mb-2">
              Bio-data form workflow: sub-county/dept committees, CHRMAC, PSB CEO, bonding letters, and term reports.
            </p>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="$router.push('/training-applications')">
              Open applications &amp; reports
            </button>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-calendar-check"></i> Attendance confirmations</h5>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="loadAttendanceSchedules">
            <i class="bx bx-refresh" :class="{ 'bx-spin': attendanceLoading }"></i> Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <p class="hr-text-muted hr-mb-2">
            Employees confirm in <strong>My Training</strong>. Review who has confirmed for each scheduled session and manage pending staff.
          </p>
          <div class="filter-bar hr-mb-3">
            <label class="filter-label">
              <span>Department</span>
              <select v-model.number="attendanceFilterDept" class="filter-select" @change="loadAttendanceSchedules">
                <option :value="0">All departments</option>
                <option v-for="d in departments" :key="'att-' + d.id" :value="Number(d.id)">{{ d.departmentName }}</option>
              </select>
            </label>
            <label class="filter-label">
              <span>When</span>
              <select v-model="attendanceWhen" class="filter-select" @change="loadAttendanceSchedules">
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <div v-if="attendanceLoading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading sessions…</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Training</th>
                  <th>Dates</th>
                  <th>Venue</th>
                  <th>Audience</th>
                  <th>Confirmed</th>
                  <th>Pending</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in attendanceSchedules" :key="s.schedule_id">
                  <td>
                    <strong>{{ s.trainingName }}</strong>
                    <div class="hr-text-muted hr-small">{{ s.trainingCode }}</div>
                  </td>
                  <td>{{ s.start_date }}<span v-if="s.end_date && s.end_date !== s.start_date"> – {{ s.end_date }}</span></td>
                  <td>{{ s.venue || '—' }}</td>
                  <td>{{ s.audience_label || '—' }}</td>
                  <td><span class="hr-badge hr-badge-success">{{ s.confirmed_count || 0 }}</span></td>
                  <td><span class="hr-badge" :class="(s.pending_count || 0) > 0 ? 'hr-badge-warning' : 'hr-badge-secondary'">{{ s.pending_count || 0 }}</span></td>
                  <td class="actions-cell">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="openAttendanceRoster(s)">View roster</button>
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="exportAttendanceCsv(s)">Export</button>
                  </td>
                </tr>
                <tr v-if="attendanceSchedules.length === 0">
                  <td colspan="7" class="hr-text-center hr-py-4 hr-text-muted">No scheduled trainings for this filter.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-file-blank"></i> Training report submissions</h5>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="loadTrainingReports">
            <i class="bx bx-refresh" :class="{ 'bx-spin': reportsLoading }"></i> Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <p class="hr-text-muted hr-mb-2">
            Reports from <strong>Employee Self-Service → My Training</strong>. Filter by the employee’s current department (from the HR register). Open documents uploaded with each report.
          </p>
          <div class="filter-bar hr-mb-3">
            <label class="filter-label">
              <span>Department</span>
              <select v-model.number="filterDepartmentId" class="filter-select" @change="loadTrainingReports">
                <option :value="0">All departments</option>
                <option v-for="d in departments" :key="d.id" :value="Number(d.id)">{{ d.departmentName }}</option>
              </select>
            </label>
          </div>
          <div v-if="reportsLoading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading reports…</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Department</th>
                  <th>Officer</th>
                  <th>Emp / PF</th>
                  <th>Training title</th>
                  <th>Past</th>
                  <th>Submitted to</th>
                  <th>Dates</th>
                  <th>Documents</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in trainingReports" :key="r.id">
                  <td>{{ r.created_at || "—" }}</td>
                  <td>{{ r.employee_department_name || "—" }}</td>
                  <td>{{ r.officer_name }}</td>
                  <td>{{ r.emp_no }}</td>
                  <td>{{ r.training_title }}</td>
                  <td>{{ pastTrainingLabel(r) }}</td>
                  <td>{{ r.submit_to_label || r.submit_target_label || "—" }}</td>
                  <td>{{ r.date_from || "—" }} → {{ r.date_to || "—" }}</td>
                  <td class="docs-cell">
                    <template v-if="docsList(r).length">
                      <button
                        v-for="(doc, idx) in docsList(r)"
                        :key="idx"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-outline-primary doc-link"
                        @click="downloadTrainingDoc(r.id, idx, doc.original_name)"
                      >
                        {{ shortDocLabel(doc, idx) }}
                      </button>
                    </template>
                    <span v-else class="hr-text-muted">—</span>
                  </td>
                  <td><span class="hr-badge hr-badge-info">{{ r.status }}</span></td>
                </tr>
                <tr v-if="trainingReports.length === 0">
                  <td colspan="10" class="hr-text-center hr-py-4 hr-text-muted">No training report submissions for this filter.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">All Trainings</h5>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="loadTrainings">
            <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
          </button>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading...</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Type</th><th>Provider</th><th>Duration</th><th>Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="t in trainings" :key="t.trainingID">
                  <td>{{ t.trainingCode }}</td>
                  <td>{{ t.trainingName }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ t.trainingType }}</span></td>
                  <td>{{ t.provider || '—' }}</td>
                  <td>{{ t.duration_days ? t.duration_days + 'd' : '' }} {{ t.duration_hours ? t.duration_hours + 'h' : '—' }}</td>
                  <td><span class="hr-badge" :class="t.is_active == 1 ? 'hr-badge-success' : 'hr-badge-secondary'">{{ t.is_active == 1 ? 'Active' : 'Inactive' }}</span></td>
                </tr>
                <tr v-if="trainings.length === 0">
                  <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No trainings found. Add some in the Trainings Catalog.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="rosterSchedule" class="hr-modal-overlay" @click.self="closeRoster">
      <div class="hr-modal roster-modal">
        <div class="hr-modal-header">
          <h3>{{ rosterSchedule.trainingName }}</h3>
          <button type="button" class="hr-modal-close" @click="closeRoster">&times;</button>
        </div>
        <div class="hr-p-3">
          <p class="hr-text-muted hr-small hr-mb-2">
            {{ rosterSchedule.start_date }}<span v-if="rosterSchedule.end_date && rosterSchedule.end_date !== rosterSchedule.start_date"> – {{ rosterSchedule.end_date }}</span>
            · {{ rosterSchedule.venue || 'Venue TBC' }} · {{ rosterDetail?.schedule?.audience_label || rosterSchedule.audience_label }}
          </p>
          <div v-if="rosterLoading" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
          <template v-else-if="rosterDetail">
            <div class="roster-tabs">
              <button type="button" class="tab-btn" :class="{ active: rosterTab === 'confirmed' }" @click="rosterTab = 'confirmed'">
                Confirmed ({{ rosterDetail.confirmed_count }})
              </button>
              <button type="button" class="tab-btn" :class="{ active: rosterTab === 'pending' }" @click="rosterTab = 'pending'">
                Pending ({{ rosterDetail.pending_count }})
              </button>
            </div>
            <div v-if="rosterMsg" class="hr-alert hr-mb-2" :class="rosterOk ? 'hr-alert-success' : 'hr-alert-danger'">{{ rosterMsg }}</div>
            <div v-show="rosterTab === 'confirmed'" class="hr-table-wrapper roster-table">
              <table class="hr-table hr-table-hover hr-table-sm">
                <thead>
                  <tr><th>Name</th><th>Emp / PF</th><th>Department</th><th>Confirmed at</th><th></th></tr>
                </thead>
                <tbody>
                  <tr v-for="a in rosterDetail.acknowledged" :key="a.ack_id">
                    <td>{{ employeeName(a) }}</td>
                    <td>{{ a.emp_no }}</td>
                    <td>{{ a.departmentName || '—' }}</td>
                    <td>{{ a.acknowledged_at }}</td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="removeAck(a)">Remove</button>
                    </td>
                  </tr>
                  <tr v-if="!rosterDetail.acknowledged.length">
                    <td colspan="5" class="hr-text-center hr-text-muted">No confirmations yet.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-show="rosterTab === 'pending'" class="hr-table-wrapper roster-table">
              <table class="hr-table hr-table-hover hr-table-sm">
                <thead>
                  <tr><th>Name</th><th>Emp / PF</th><th>Department</th><th>Designation</th><th></th></tr>
                </thead>
                <tbody>
                  <tr v-for="p in rosterDetail.pending" :key="p.nationalId">
                    <td>{{ employeeName(p) }}</td>
                    <td>{{ p.empNo }}</td>
                    <td>{{ p.departmentName || '—' }}</td>
                    <td>{{ p.positionTitle || '—' }}</td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="adminConfirm(p)">Mark confirmed</button>
                    </td>
                  </tr>
                  <tr v-if="!rosterDetail.pending.length">
                    <td colspan="5" class="hr-text-center hr-text-muted">Everyone eligible has confirmed.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, executeDownload } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

export default {
  name: "TrainingManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      reportsLoading: false,
      exporting: false,
      exportingPdf: false,
      letterhead: null,
      trainings: [],
      trainingReports: [],
      departments: [],
      filterDepartmentId: 0,
      attendanceLoading: false,
      attendanceSchedules: [],
      attendanceFilterDept: 0,
      attendanceWhen: "upcoming",
      rosterSchedule: null,
      rosterDetail: null,
      rosterLoading: false,
      rosterTab: "confirmed",
      rosterMsg: "",
      rosterOk: false,
      breadcrumbItems: [
        { label: "Dashboard", route: "/dashboard" },
        { label: "Training Management Module" },
        { label: "Training Management" }
      ]
    };
  },
  computed: {
    activeCount() {
      return this.trainings.filter(t => t.is_active == 1).length;
    }
  },
  mounted() {
    this.loadLetterhead();
    this.loadDepartments();
    this.loadTrainings();
    this.loadAttendanceSchedules();
    this.loadTrainingReports();
  },
  methods: {
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try { return new URL(u, authUrl).href; } catch (_) { return u; }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    exportReportsCsv() {
      this.exporting = true;
      try {
        const headers = ["Date", "Department", "Officer", "Emp No", "Training", "Past", "Submitted to", "From", "To", "Status"];
        const body = this.trainingReports.map((r) => [
          r.created_at || "",
          r.employee_department_name || "",
          r.officer_name || "",
          r.emp_no || "",
          r.training_title || "",
          this.pastTrainingLabel(r),
          r.submit_to_label || r.submit_target_label || "",
          r.date_from || "",
          r.date_to || "",
          r.status || ""
        ]);
        const csv = [headers, ...body].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `training_management_reports_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
    async exportReportsPdf() {
      this.exportingPdf = true;
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const startY = addCountyLetterhead(doc, {
          title: "TRAINING MANAGEMENT — REPORT SUBMISSIONS",
          subtitle: this.filterDepartmentId
            ? `Department filter applied`
            : "All departments",
          periodLabel: new Date().toLocaleString("en-KE"),
          logoDataUrl,
          letterhead: this.letterhead
        });
        autoTable(doc, {
          startY,
          head: [["Date", "Dept", "Officer", "Emp No", "Training", "From", "To", "Status"]],
          body: this.trainingReports.map((r) => [
            r.created_at || "",
            r.employee_department_name || "",
            r.officer_name || "",
            r.emp_no || "",
            r.training_title || "",
            r.date_from || "",
            r.date_to || "",
            r.status || ""
          ]),
          styles: { fontSize: 7 },
          headStyles: { fillColor: [12, 74, 110], textColor: 255 },
          margin: { left: 10, right: 10 }
        });
        const pageH = doc.internal.pageSize.getHeight();
        const pageW = doc.internal.pageSize.getWidth();
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
          doc.setPage(i);
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          doc.text(
            (this.letterhead && this.letterhead.reportFooter) || "Official HRMIS document — County Government of Homa Bay",
            14,
            pageH - 8
          );
          doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: "right" });
        }
        doc.save(`training_management_reports_${new Date().toISOString().slice(0, 10)}.pdf`);
      } catch (e) {
        window.alert(e?.message || "PDF export failed");
      } finally {
        this.exportingPdf = false;
      }
    },
    pastTrainingLabel(r) {
      const v = r.is_past_training;
      if (v === 1 || v === "1" || v === true) return "Yes";
      if (v === 0 || v === "0" || v === false) return "No";
      return "—";
    },
    docsList(r) {
      try {
        const raw = r.uploaded_documents;
        if (!raw || typeof raw !== "string") return [];
        const d = JSON.parse(raw);
        return Array.isArray(d) ? d : [];
      } catch (_) {
        return [];
      }
    },
    shortDocLabel(doc, idx) {
      const lab = (doc && doc.label) || `File ${idx + 1}`;
      return lab.length > 22 ? lab.slice(0, 20) + "…" : lab;
    },
    async loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      try {
        const res = await execute(data, authUrl);
        if (res.data?.success && Array.isArray(res.data.data?.departments)) {
          this.departments = res.data.data.departments;
        } else {
          this.departments = [];
        }
      } catch (_) {
        this.departments = [];
      }
    },
    async loadTrainingReports() {
      this.reportsLoading = true;
      const data = new FormData();
      data.append("function", "listTrainingReportSubmissions");
      if (this.filterDepartmentId > 0) {
        data.append("departmentId", String(this.filterDepartmentId));
      }
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.trainingReports = Array.isArray(d?.reports) ? d.reports : [];
      } catch (_) {
        this.trainingReports = [];
      } finally {
        this.reportsLoading = false;
      }
    },
    async downloadTrainingDoc(reportId, slot, suggestedName) {
      try {
        const fd = new FormData();
        fd.append("function", "downloadTrainingReportFile");
        fd.append("reportId", String(reportId));
        fd.append("slot", String(slot));
        const res = await executeDownload(fd, authUrl);
        const ct = (res.headers && res.headers["content-type"]) || "";
        if (ct.indexOf("application/json") !== -1) {
          const rawText = res.data instanceof Blob ? await res.data.text() : String(res.data);
          let msg = "Download failed.";
          try {
            const j = JSON.parse(rawText);
            if (j.message) msg = j.message;
          } catch (_) {}
          alert(msg);
          return;
        }
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: ct || "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const base = (suggestedName && String(suggestedName).trim()) || "training-attachment";
        const safe = base.replace(/[^\w.\-()\s]/g, "_").slice(0, 180) || "attachment";
        a.download = safe;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        alert(e?.message || "Download failed.");
      }
    },
    async loadTrainings() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getTrainings");
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.trainings = Array.isArray(d?.trainings) ? d.trainings : [];
      } catch (_) {
        this.trainings = [];
      } finally {
        this.loading = false;
      }
    },
    async loadAttendanceSchedules() {
      this.attendanceLoading = true;
      const fd = new FormData();
      fd.append("function", "listTrainingSchedulesForManagement");
      fd.append("when", this.attendanceWhen);
      if (this.attendanceFilterDept > 0) {
        fd.append("departmentId", String(this.attendanceFilterDept));
      }
      try {
        const res = await execute(fd, authUrl);
        this.attendanceSchedules = res.data?.success && Array.isArray(res.data.data?.schedules)
          ? res.data.data.schedules
          : [];
      } catch (_) {
        this.attendanceSchedules = [];
      } finally {
        this.attendanceLoading = false;
      }
    },
    async openAttendanceRoster(s) {
      this.rosterSchedule = { ...s };
      this.rosterDetail = null;
      this.rosterTab = "confirmed";
      this.rosterMsg = "";
      await this.loadRosterDetail();
    },
    closeRoster() {
      this.rosterSchedule = null;
      this.rosterDetail = null;
      this.rosterMsg = "";
    },
    async loadRosterDetail() {
      if (!this.rosterSchedule) return;
      this.rosterLoading = true;
      const fd = new FormData();
      fd.append("function", "getTrainingScheduleAttendanceDetail");
      fd.append("scheduleID", String(this.rosterSchedule.schedule_id));
      if (this.attendanceFilterDept > 0) {
        fd.append("departmentId", String(this.attendanceFilterDept));
      }
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.rosterDetail = res.data.data;
        } else {
          this.rosterMsg = res.data?.message || "Could not load roster";
          this.rosterOk = false;
        }
      } catch (e) {
        this.rosterMsg = e?.message || "Could not load roster";
        this.rosterOk = false;
      } finally {
        this.rosterLoading = false;
      }
    },
    employeeName(row) {
      const n = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
      return n || row.officer_name || "—";
    },
    async adminConfirm(emp) {
      if (!this.rosterSchedule) return;
      const fd = new FormData();
      fd.append("function", "adminConfirmTrainingAttendance");
      fd.append("scheduleID", String(this.rosterSchedule.schedule_id));
      fd.append("nationalId", emp.nationalId || "");
      fd.append("empNo", emp.empNo || "");
      try {
        const res = await execute(fd, authUrl);
        this.rosterOk = !!res.data?.success;
        this.rosterMsg = res.data?.message || (this.rosterOk ? "Updated." : "Failed.");
        if (this.rosterOk) {
          await this.loadRosterDetail();
          await this.loadAttendanceSchedules();
        }
      } catch (e) {
        this.rosterOk = false;
        this.rosterMsg = e?.message || "Failed.";
      }
    },
    async removeAck(row) {
      if (!row.ack_id || !confirm("Remove this attendance confirmation?")) return;
      const fd = new FormData();
      fd.append("function", "adminRemoveTrainingAcknowledgement");
      fd.append("ackID", String(row.ack_id));
      try {
        const res = await execute(fd, authUrl);
        this.rosterOk = !!res.data?.success;
        this.rosterMsg = res.data?.message || (this.rosterOk ? "Removed." : "Failed.");
        if (this.rosterOk) {
          await this.loadRosterDetail();
          await this.loadAttendanceSchedules();
        }
      } catch (e) {
        this.rosterOk = false;
        this.rosterMsg = e?.message || "Failed.";
      }
    },
    async exportAttendanceCsv(s) {
      this.rosterSchedule = { ...s };
      await this.loadRosterDetail();
      if (!this.rosterDetail) {
        alert(this.rosterMsg || "Could not load data for export.");
        return;
      }
      const lines = ["Type,Name,Emp/PF,Department,Designation,Confirmed at"];
      (this.rosterDetail.acknowledged || []).forEach((a) => {
        lines.push([
          "Confirmed",
          this.csvEscape(this.employeeName(a)),
          this.csvEscape(a.emp_no),
          this.csvEscape(a.departmentName),
          this.csvEscape(a.positionTitle),
          this.csvEscape(a.acknowledged_at)
        ].join(","));
      });
      (this.rosterDetail.pending || []).forEach((p) => {
        lines.push([
          "Pending",
          this.csvEscape(this.employeeName(p)),
          this.csvEscape(p.empNo),
          this.csvEscape(p.departmentName),
          this.csvEscape(p.positionTitle),
          ""
        ].join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `training-attendance-${s.trainingCode || s.schedule_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      this.rosterSchedule = null;
      this.rosterDetail = null;
    },
    csvEscape(v) {
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }
  }
};
</script>

<style scoped>
.training-management-content { padding: 0; }
.grid-cards { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.filter-label span {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}
.filter-select {
  min-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
}
.docs-cell {
  white-space: normal;
  max-width: 220px;
}
.doc-link {
  display: inline-block;
  margin: 2px 4px 2px 0;
  font-size: 11px;
}
.hr-small { font-size: 0.8rem; }
.hr-mb-2 { margin-bottom: 8px; }
.actions-cell { white-space: nowrap; }
.actions-cell .hr-btn { margin-right: 4px; }
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}
.roster-modal {
  background: #fff;
  border-radius: 16px;
  width: min(920px, 96vw);
  max-height: 90vh;
  overflow-y: auto;
}
.hr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.hr-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #64748b;
}
.hr-p-3 { padding: 16px 20px 20px; }
.roster-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.tab-btn {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  font-size: 0.875rem;
}
.tab-btn.active {
  background: #059669;
  color: #fff;
  border-color: #059669;
}
.roster-table { max-height: 360px; overflow-y: auto; }
.hr-table-sm td, .hr-table-sm th { font-size: 0.85rem; padding: 8px 10px; }
.hr-alert {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.9rem;
}
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
</style>
